import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../utils/helpers';
import Handlebars from 'handlebars';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import TemplateModal from '../components/modals/TemplateModal';
import api from "../utils/api"
import { API_CONFIG } from '../utils/apiConfig';
import useDashboardData from '../contexts/userDashboardData';

// Register Handlebars helpers
Handlebars.registerHelper('multiply', (a, b) => a * b);
Handlebars.registerHelper('calculateTax', (amount, rate) => amount * (rate / 100));
Handlebars.registerHelper('daysBetween', (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
});

const AllInvoicesPage = () => {
  const { currentUser } = useAuth();
  const { data: dashboardData, company: dashboardCompany } = useDashboardData();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState({});
  const [displayCurrency, setDisplayCurrency] = useState('NGN');
  
  // Pagination state
  const [paginationMode, setPaginationMode] = useState(() => {
    return localStorage.getItem('invoicePaginationMode') || 'load-more';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalInvoices, setTotalInvoices] = useState(0);
  
  // Refs
  const observerTarget = useRef(null);
  const scrollContainerRef = useRef(null);
  
  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
    totalAmount: 0,
    paidAmount: 0
  });
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  // Get view mode from localStorage or default to 'list'
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('invoiceViewMode');
    return savedViewMode || 'list';
  });

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('invoiceViewMode', viewMode);
  }, [viewMode]);

  // Save pagination mode to localStorage
  useEffect(() => {
    localStorage.setItem('invoicePaginationMode', paginationMode);
  }, [paginationMode]);

  // Set initial display currency based on dashboard data
  useEffect(() => {
    if (dashboardData?.currency) {
      setDisplayCurrency(dashboardData.currency);
    }
  }, [dashboardData]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setInvoices([]);
    setFilteredInvoices([]);
    setHasMore(true);
    fetchInvoices(1, true);
  }, [searchTerm, statusFilter, dateFilter]);

  // Load templates function
  const loadTemplates = useCallback(async () => {
    try {
      const templateFiles = [
        'modern-classic',
        'minimalist',
        'corporate',
        'creative',
        'premium'
      ];
      
      const loadedTemplates = {};
      
      for (const templateName of templateFiles) {
        try {
          const response = await fetch(`/templates/${templateName}.hbs`);
          if (response.ok) {
            const templateContent = await response.text();
            loadedTemplates[templateName] = Handlebars.compile(templateContent);
          }
        } catch (error) {
          console.error(`Error loading template ${templateName}:`, error);
        }
      }
      
      setTemplates(loadedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  }, []);

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Fetch invoices from API with pagination
  const fetchInvoices = async (page = 1, isReset = false) => {
    if (isReset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const params = {
        page,
        limit: 9,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      };

      // Add date range filter if needed
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate, endDate;

        switch (dateFilter) {
          case 'today':
            startDate = now.toISOString().split('T')[0];
            endDate = startDate;
            break;
          case 'week':
            const weekAgo = new Date(now);
            weekAgo.setDate(now.getDate() - 7);
            startDate = weekAgo.toISOString().split('T')[0];
            endDate = now.toISOString().split('T')[0];
            break;
          case 'month':
            const monthAgo = new Date(now);
            monthAgo.setMonth(now.getMonth() - 1);
            startDate = monthAgo.toISOString().split('T')[0];
            endDate = now.toISOString().split('T')[0];
            break;
          default:
            break;
        }

        if (startDate && endDate) {
          params.startDate = startDate;
          params.endDate = endDate;
        }
      }

    const response = await api.get(API_CONFIG.INVOICES.MY_INVOICES, {params});
      
      if (response.data.success) {
        const newInvoices = response.data.invoices || [];
        const pagination = response.data.pagination || {};
        
        if (isReset) {
          setInvoices(newInvoices);
          setFilteredInvoices(newInvoices);
        } else {
          setInvoices(prev => [...prev, ...newInvoices]);
          setFilteredInvoices(prev => [...prev, ...newInvoices]);
        }

        setTotalInvoices(pagination.totalInvoices || 0);
        setHasMore(page < (pagination.totalPages || 1));
        
        if (!isReset && newInvoices.length === 0) {
          showToast('No more invoices to load', 'info');
        }
      } else {
        showToast('Failed to load invoices', 'error');
        if (isReset) {
          setInvoices([]);
          setFilteredInvoices([]);
        }
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      showToast('Failed to load invoices', 'error');
      if (isReset) {
        setInvoices([]);
        setFilteredInvoices([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchInvoices(1, true);
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (paginationMode !== 'infinite-scroll' || !hasMore || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage(prev => {
            const nextPage = prev + 1;
            fetchInvoices(nextPage, false);
            return nextPage;
          });
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [paginationMode, hasMore, isLoading, isLoadingMore]);

  // Load more button handler
  const handleLoadMore = () => {
    if (!hasMore || isLoadingMore) return;
    
    setCurrentPage(prev => {
      const nextPage = prev + 1;
      fetchInvoices(nextPage, false);
      return nextPage;
    });
  };

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      setIsStatsLoading(true);
      try {
        const statsData = await getTotalStats();
        setStats(statsData);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setIsStatsLoading(false);
      }
    };

    fetchStats();
  }, [invoices, displayCurrency]);

  const getTotalStats = async () => {
    try {
      const response = await api.post(API_CONFIG.INVOICES.MY_TOTAL_INVOICES);
      
      if(response.data.success) {
        const invoices = response.data.invoices;
        const total = invoices.length;
        const paid = invoices.filter(i => i.status === 'paid').length;
        const pending = invoices.filter(i => i.status === 'pending').length;
        const overdue = invoices.filter(i => i.status === 'overdue').length;

        const totalAmount = invoices.reduce((sum, invoice) => {
          const amountInDisplayCurrency = convertCurrency(
            Number(invoice.amount) || 0, 
            invoice.currency || 'NGN', 
            displayCurrency
          );
          return sum + amountInDisplayCurrency;
        }, 0);

        const paidAmount = invoices
          .filter(i => i.status === 'paid')
          .reduce((sum, invoice) => {
            const amountInDisplayCurrency = convertCurrency(
              invoice.amount || 0, 
              invoice.currency || 'NGN', 
              displayCurrency
            );
            return sum + amountInDisplayCurrency;
          }, 0);

        return { total, paid, pending, overdue, totalAmount, paidAmount };
      }
    } catch (error) {
      console.error('Error fetching total stats:', error);
    }
    
    return { total: 0, paid: 0, pending: 0, overdue: 0, totalAmount: 0, paidAmount: 0 };
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      paid: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      overdue: "bg-red-100 text-red-800",
      draft: "bg-gray-100 "
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.draft}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Draft'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    let date;

    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      date = new Date(dateString.replace(' ', 'T'));
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date)) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = displayCurrency) => {
    if (!amount) return displayCurrency === 'NGN' ? '₦0.00' : '$0.00';
    
    const options = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    };
    
    return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', options).format(amount);
  };

  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    const conversionRates = {
      NGN: { USD: 0.0012 },
      USD: { NGN: 833.33 }
    };
    
    if (conversionRates[fromCurrency] && conversionRates[fromCurrency][toCurrency]) {
      return amount * conversionRates[fromCurrency][toCurrency];
    }
    
    return amount;
  };

  const toggleCurrency = () => {
    setDisplayCurrency(prev => prev === 'NGN' ? 'USD' : 'NGN');
  };

const generatePDF = async (invoiceData, templateId) => {
  if (!templates[templateId]) {
    showToast('Template not found', 'error');
    return false;
  }

  try {
    // Enhance invoice data with owner company information
    const enhancedInvoiceData = {
      ...invoiceData,
      company: invoiceData.owner_company || {
        name: currentUser?.company_name || currentUser?.fullname || '',
        email: currentUser?.company_email || '',
        phone: currentUser?.company_phone || '',
        address: currentUser?.company_address || '',
        logo: currentUser?.company_logo || '',
        website: currentUser?.company_website || '',
        reg_number: currentUser?.reg_number || '',
        bank: {
          name: currentUser?.bank_name || '',
          account_number: currentUser?.account_number || '',
          account_name: currentUser?.account_name || '',
          swift_code: currentUser?.swift_code || '',
          address: currentUser?.bank_address || ''
        }
      },
      // Format dates consistently
      formatted_date: formatDate(invoiceData.date),
      formatted_due_date: formatDate(invoiceData.due_date),
      // Calculate totals
      subtotal: invoiceData.items?.reduce((sum, item) => 
        sum + (item.price * item.quantity), 0
      ) || 0,
      tax_total: invoiceData.items?.reduce((sum, item) => 
        sum + (item.price * item.quantity * (item.tax_rate || 0) / 100), 0
      ) || 0,
      // Format currency based on invoice currency
      format_currency: (amount) => {
        return new Intl.NumberFormat(
          invoiceData.currency === 'NGN' ? 'en-NG' : 'en-US', 
          {
            style: 'currency',
            currency: invoiceData.currency || 'NGN',
            minimumFractionDigits: 2
          }
        ).format(amount);
      }
    };

    const htmlContent = templates[templateId](enhancedInvoiceData);
    const tempDiv = document.createElement('div');
    tempDiv.style.width = '210mm';
    tempDiv.style.minHeight = '297mm';
    tempDiv.style.padding = '20px';
    tempDiv.style.boxSizing = 'border-box';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.style.background = 'white';
    tempDiv.style.fontFamily = "'Arial', 'Helvetica', sans-serif";
    
    tempDiv.innerHTML = htmlContent;
    document.body.appendChild(tempDiv);
    
    // Wait for images to load
    const images = tempDiv.getElementsByTagName('img');
    if (images.length > 0) {
      await Promise.all(Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = resolve; // Don't reject on image error, just continue
          setTimeout(resolve, 1000); // Timeout after 1 second
        });
      }));
    }
    
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      foreignObjectRendering: false
    });
    
    document.body.removeChild(tempDiv);
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${invoiceData.invoice_number}-${templateId}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    
    // Fallback simple PDF generation with owner company info
    try {
      const ownerCompany = invoiceData.owner_company || {
        name: currentUser?.company_name || currentUser?.fullname || 'Company Name',
        email: currentUser?.company_email || '',
        phone: currentUser?.company_phone || '',
        address: currentUser?.company_address || '',
        logo: currentUser?.company_logo || '',
        bank: {
          name: currentUser?.bank_name || '',
          account_number: currentUser?.account_number || '',
          account_name: currentUser?.account_name || '',
        }
      };

      const pdf = new jsPDF();
      let yPos = 20;
      
      // Add company logo if available
      if (ownerCompany.logo) {
        try {
          pdf.addImage(ownerCompany.logo, 'JPEG', 20, yPos, 40, 20);
          yPos += 25;
        } catch (imgError) {
          console.error('Error adding logo:', imgError);
        }
      }
      
      // Company info
      pdf.setFontSize(14);
      pdf.text(ownerCompany.name || 'Company Name', 20, yPos);
      yPos += 7;
      
      pdf.setFontSize(8);
      if (ownerCompany.address) {
        pdf.text(ownerCompany.address, 20, yPos);
        yPos += 5;
      }
      if (ownerCompany.email) {
        pdf.text(ownerCompany.email, 20, yPos);
        yPos += 5;
      }
      if (ownerCompany.phone) {
        pdf.text(ownerCompany.phone, 20, yPos);
        yPos += 5;
      }
      
      yPos += 10;
      
      // Invoice header
      pdf.setFontSize(16);
      pdf.text(`INVOICE: ${invoiceData.invoice_number}`, 20, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      pdf.text(`Client: ${invoiceData.client_name}`, 20, yPos);
      yPos += 5;
      if (invoiceData.client_email) {
        pdf.text(`Email: ${invoiceData.client_email}`, 20, yPos);
        yPos += 5;
      }
      yPos += 5;
      
      pdf.text(`Date: ${formatDate(invoiceData.date)}`, 20, yPos);
      yPos += 5;
      pdf.text(`Due Date: ${formatDate(invoiceData.due_date)}`, 20, yPos);
      yPos += 5;
      pdf.text(`Status: ${invoiceData.status}`, 20, yPos);
      yPos += 10;
      
      // Items table
      pdf.setFontSize(12);
      pdf.text('Items:', 20, yPos);
      yPos += 7;
      
      pdf.setFontSize(9);
      invoiceData.items?.forEach((item, index) => {
        if (yPos > 250) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.text(`${item.description}`, 20, yPos);
        yPos += 4;
        pdf.text(`  Qty: ${item.quantity} × ${formatCurrency(item.price, invoiceData.currency)}`, 25, yPos);
        if (item.tax_rate > 0) {
          pdf.text(`  Tax: ${item.tax_rate}%`, 25, yPos + 4);
          yPos += 4;
        }
        yPos += 6;
      });
      
      yPos += 5;
      
      // Total
      pdf.setFontSize(11);
      pdf.text(`Total: ${formatCurrency(invoiceData.amount, invoiceData.currency)}`, 20, yPos);
      
      // Bank details
      if (ownerCompany.bank) {
        yPos += 10;
        pdf.setFontSize(10);
        pdf.text('Bank Details:', 20, yPos);
        yPos += 5;
        pdf.setFontSize(8);
        pdf.text(`Bank: ${ownerCompany.bank.name || 'N/A'}`, 20, yPos);
        yPos += 4;
        pdf.text(`Account: ${ownerCompany.bank.account_number || 'N/A'}`, 20, yPos);
        yPos += 4;
        pdf.text(`Name: ${ownerCompany.bank.account_name || 'N/A'}`, 20, yPos);
      }
      
      pdf.save(`${invoiceData.invoice_number}-simple.pdf`);
      showToast('PDF downloaded (simple format)', 'info');
      return true;
    } catch (fallbackError) {
      console.error('Fallback PDF generation also failed:', fallbackError);
      showToast('Failed to generate PDF', 'error');
      return false;
    }
  }
};

  const handleDownloadPDF = async (templateId) => {
    if (!selectedInvoice) return;

    setIsLoading(true);
    setShowTemplateModal(false);
    
    try {
      showToast(`Generating PDF...`, 'info');
      
      const success = await generatePDF(selectedInvoice, templateId);
      
      if (success) {
        showToast('PDF downloaded successfully!', 'success');
      } else {
        showToast('Failed to generate PDF', 'error');
      }
      
    } catch (error) {
      showToast('Failed to generate PDF', 'error');
      console.error('PDF generation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openTemplateModal = () => {
    setShowTemplateModal(true);
  };

  if (isLoading && invoices.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-7 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-lg p-3 shadow">
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <div className="h-48 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4" ref={scrollContainerRef}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h1 className="text-lg font-bold ">All Invoices</h1>
              <p className="text-xs text-gray-500 mt-1">Manage and track all your invoices</p>
            </div>
            <Link
              to="/new/invoice"
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              New Invoice
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="flex flex-col mb-4">
          {/* First row - 4 cards in 2x2 grid */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Total Invoices */}
            <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Invoices</p>
                  {isStatsLoading ? (
                    <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  ) : (
                    <p className="text-base font-bold ">{stats.total}</p>
                  )}
                </div>
                <div className="p-1 bg-blue-100 rounded-lg">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Paid */}
            <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Paid</p>
                  {isStatsLoading ? (
                    <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  ) : (
                    <p className="text-base font-bold text-green-600">{stats.paid}</p>
                  )}
                </div>
                <div className="p-1 bg-green-100 rounded-lg">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Pending */}
            <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  {isStatsLoading ? (
                    <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  ) : (
                    <p className="text-base font-bold text-yellow-600">{stats.pending}</p>
                  )}
                </div>
                <div className="p-1 bg-yellow-100 rounded-lg">
                  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Overdue */}
            <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Overdue</p>
                  {isStatsLoading ? (
                    <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  ) : (
                    <p className="text-base font-bold text-red-600">{stats.overdue}</p>
                  )}
                </div>
                <div className="p-1 bg-red-100 rounded-lg">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Second row - Total Amount card */}
          <div className="w-full">
            <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <div className="flex items-center gap-2 min-w-0">
                    {isStatsLoading ? (
                      <div className="h-5 bg-gray-200 rounded animate-pulse w-24"></div>
                    ) : (
                      <>
                        <p className="text-base font-bold  truncate">
                          {formatCurrency(stats.totalAmount)}
                        </p>
                        <button
                          onClick={toggleCurrency}
                          className="flex-shrink-0 text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                          title={`Switch to ${displayCurrency === 'NGN' ? 'USD' : 'NGN'}`}
                        >
                          {displayCurrency === 'NGN' ? 'USD' : 'NGN'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="p-1 bg-purple-100 rounded-lg flex-shrink-0 ml-2">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200 mb-4">
          <div className="flex flex-col gap-3">
            {/* Search */}
            <div className="w-full">
              <label htmlFor="search" className="sr-only">Search invoices</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search invoices..."
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap gap-2">
              <select
                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm flex-1 min-w-0"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="draft">Draft</option>
              </select>

              <select
                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm flex-1 min-w-0"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>

              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  className={`px-2 py-2 ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600'}`}
                  onClick={() => setViewMode('list')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                  </svg>
                </button>
                <button
                  className={`px-2 py-2 ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600'}`}
                  onClick={() => setViewMode('grid')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
                  </svg>
                </button>
              </div>

              {/* Pagination Mode Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  className={`px-2 py-2 text-xs ${paginationMode === 'load-more' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600'}`}
                  onClick={() => setPaginationMode('load-more')}
                  title="Load More Button"
                >
                  Load More
                </button>
                <button
                  className={`px-2 py-2 text-xs ${paginationMode === 'infinite-scroll' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600'}`}
                  onClick={() => setPaginationMode('infinite-scroll')}
                  title="Infinite Scroll"
                >
                  Auto Load
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-3">
          <p className="text-xs text-gray-600">
            Showing {invoices.length} of {totalInvoices} invoices
            {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && ' (filtered)'}
            {currentPage > 1 && ` - Page ${currentPage}`}
          </p>
        </div>

        {/* Invoices List */}
        {invoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6 text-center">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <h3 className="text-sm font-semibold  mb-1">No invoices found</h3>
            <p className="text-xs text-gray-500 mb-3">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setDateFilter('all');
              }}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
            >
              Clear Filters
            </button>
          </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {invoices.map(invoice => (
                <div
                  key={invoice.id}
                  className="p-3 hover:bg-gray-100 transition-colors duration-150 cursor-pointer group"
                  onClick={() => setSelectedInvoice(invoice)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                        {invoice.invoice_number}
                      </span>
                      <div className="flex-shrink-0">
                        {getStatusBadge(invoice.status)}
                      </div>
                    </div>
                    <span className="text-sm font-bold text-gray-900 ml-2 flex-shrink-0">
                      {formatCurrency(
                        convertCurrency(invoice.amount || 0, invoice.currency || 'NGN', displayCurrency),
                        displayCurrency
                      )}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-600 truncate min-w-0 flex-1">
                      <span className="truncate">{invoice.client_name}</span>
                      {invoice.client_email && (
                        <span className="text-gray-500 ml-1 hidden xs:inline">• {invoice.client_email}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 ml-2 flex-shrink-0">
                      {formatDate(invoice.date)}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Due: {formatDate(invoice.due_date)}
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-xs text-blue-600 font-medium flex items-center">
                        View
                        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {invoices.map(invoice => (
              <div
                key={invoice.id}
                className="bg-white rounded-lg shadow border border-gray-200 p-3 hover:shadow-md transition-all duration-200 cursor-pointer group"
                onClick={() => setSelectedInvoice(invoice)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate flex-1 mr-2">
                    {invoice.invoice_number}
                  </span>
                  {getStatusBadge(invoice.status)}
                </div>

                <div className="mb-2">
                  <p className="text-sm font-bold text-gray-900 mb-1 truncate">
                    {formatCurrency(
                      convertCurrency(invoice.amount || 0, invoice.currency || 'NGN', displayCurrency),
                      displayCurrency
                    )}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{invoice.client_name}</p>
                  {invoice.client_email && (
                    <p className="text-xs text-gray-500 truncate">{invoice.client_email}</p>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-2">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span className="truncate mr-2">Issued: {formatDate(invoice.date)}</span>
                    <span className="flex-shrink-0">Due: {formatDate(invoice.due_date)}</span>
                  </div>
                </div>

                <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-xs text-blue-600 font-medium flex items-center justify-center">
                    View details
                    <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading More Indicator */}
        {isLoadingMore && (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading more invoices...</span>
          </div>
        )}

        {/* Load More Button or Observer Target */}
        {hasMore && paginationMode === 'load-more' && (
          <div className="flex justify-center mt-4">
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm ${
                isLoadingMore ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {/* Infinite Scroll Observer Target */}
        {hasMore && paginationMode === 'infinite-scroll' && (
          <div ref={observerTarget} className="h-4" />
        )}

        {/* No More Results */}
        {!hasMore && invoices.length > 0 && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">No more invoices to load</p>
          </div>
        )}
      </div>

 {/* Invoice Detail Modal - Add Owner Company Information */}
{selectedInvoice && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-bold">Invoice Details</h2>
        <button
          onClick={() => setSelectedInvoice(null)}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <div className="space-y-4">
        {/* Owner Company Information */}
        {selectedInvoice.owner_company && (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-700">Company Information</h3>
              {selectedInvoice.owner_company.logo && (
                <img 
                  src={selectedInvoice.owner_company.logo} 
                  alt="Company Logo" 
                  className="h-8 w-auto object-contain"
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-gray-500">Company Name</p>
                <p className="font-medium truncate">{selectedInvoice.owner_company.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-500">Reg Number</p>
                <p className="font-medium truncate">{selectedInvoice.owner_company.reg_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium truncate">{selectedInvoice.owner_company.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-500">Phone</p>
                <p className="font-medium truncate">{selectedInvoice.owner_company.phone || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-500">Address</p>
                <p className="font-medium">{selectedInvoice.owner_company.address || 'N/A'}</p>
              </div>
            </div>

            {/* Bank Information */}
            {selectedInvoice.owner_company.bank && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <h4 className="text-xs font-semibold text-gray-700 mb-1">Bank Details</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Bank Name</p>
                    <p className="font-medium truncate">{selectedInvoice.owner_company.bank.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Account Number</p>
                    <p className="font-medium truncate">{selectedInvoice.owner_company.bank.account_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Account Name</p>
                    <p className="font-medium truncate">{selectedInvoice.owner_company.bank.account_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">SWIFT Code</p>
                    <p className="font-medium truncate">{selectedInvoice.owner_company.bank.swift_code || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invoice Details */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500">Invoice Number</p>
            <p className="text-sm font-semibold truncate">{selectedInvoice.invoice_number}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Status</p>
            <div className="text-sm">{getStatusBadge(selectedInvoice.status)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500">Client</p>
            <p className="text-sm font-semibold truncate">{selectedInvoice.client_name}</p>
            <p className="text-xs text-gray-600 truncate">{selectedInvoice.client_email}</p>
            {selectedInvoice.client_phone && (
              <p className="text-xs text-gray-600 truncate">{selectedInvoice.client_phone}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500">Amount</p>
            <p className="text-sm font-semibold truncate">
              {formatCurrency(
                convertCurrency(selectedInvoice.amount || 0, selectedInvoice.currency || 'NGN', displayCurrency),
                displayCurrency
              )}
            </p>
            <p className="text-xs text-gray-600">Currency: {selectedInvoice.currency || 'NGN'}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500">Issue Date</p>
            <p className="text-sm">{formatDate(selectedInvoice.date)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Due Date</p>
            <p className="text-sm">{formatDate(selectedInvoice.due_date)}</p>
          </div>
        </div>

        {selectedInvoice.narration && (
          <div>
            <p className="text-xs text-gray-500">Narration</p>
            <p className="text-sm bg-gray-50 p-2 rounded">{selectedInvoice.narration}</p>
          </div>
        )}

        <div>
          <p className="text-xs text-gray-500 mb-2">Items</p>
          <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
            {selectedInvoice.items?.map((item, index) => (
              <div key={index} className="p-2 flex justify-between text-xs">
                <div className="min-w-0 flex-1 mr-2">
                  <p className="font-medium truncate">{item.description}</p>
                  <p className="text-gray-500">
                    Qty: {item.quantity} × {formatCurrency(item.price, selectedInvoice.currency)}
                    {item.tax_rate > 0 && ` (Tax: ${item.tax_rate}%)`}
                  </p>
                </div>
                <p className="flex-shrink-0 font-medium">
                  {formatCurrency(
                    convertCurrency(item.total, selectedInvoice.currency || 'NGN', displayCurrency),
                    displayCurrency
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-3 border-t">
          <button 
            onClick={openTemplateModal}
            className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-xs"
          >
            Download PDF
          </button>
          <button className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs">
            Send Reminder
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {/* Template Selection Modal */}
      {showTemplateModal && (
        <TemplateModal
          show={showTemplateModal}
          onHide={() => setShowTemplateModal(false)}
          onSelectTemplate={handleDownloadPDF}
        />   
      )}
    </div>
  );
};

export default AllInvoicesPage;